import { useState } from 'react';

import { artworkUrl } from '@shared/assets/artwork';
import { formatNumber } from '@shared/format';

import { toAcceptAttribute, useSubjectArtworks } from '../_shared/queries';
import { useUploadSubjectArtwork } from '../_shared/mutations';
import type { UploadPolicy } from '../_shared/types';
import { copy } from './copy';

/**
 * The one image a season or an episode carries, and the control that supplies it.
 *
 * **This block exists because the purpose did and the surface did not.**
 * `ARTWORK_PURPOSES` has named `GROUP: thumbnail:required` and `MEDIA_ASSET:
 * thumbnail:optional` since the service was written, `POST /artworks` accepts
 * both subject types, and the subscriber's episode list reads them — but the
 * console offered an upload for a title, an editorial post and a credited
 * person only. So every episode row on the subscriber app drew its gradient
 * stand-in, and no operator had any way to change that.
 *
 * **It reads and writes its own subject rather than taking callbacks.** The
 * documents block beside it already does the same for the same reason: the
 * control is complete in itself — one subject in, one image out — and threading
 * a read, a mutation and a busy flag for it through the page, the pane and the
 * row would put four parameters on three components to serve one box.
 *
 * **The shape and the floor are READ, never drawn.** A season's thumbnail is a
 * `2:3` poster and an episode's is a `16:9` still; both come off `/uploadPolicy`
 * with the minimum geometry the service will accept, so the aspect box here
 * matches what the crop will actually produce and the hint states the floor
 * before the file is transferred rather than after — the refusal is
 * `422 ASSETS_ARTWORK_TOO_SMALL` and it arrives at the end of the upload.
 */
export function ArtworkControl({
  subjectType,
  subjectId,
  subjectName,
  uploadPolicy,
}: {
  /** `GROUP` for a season, `MEDIA_ASSET` for an episode — the service's own literal. */
  subjectType: 'GROUP' | 'MEDIA_ASSET';
  subjectId: string;
  /** Named in the control's label, so a page of nine rows has nine distinct controls. */
  subjectName: string;
  uploadPolicy: UploadPolicy | undefined;
}) {
  const [open, setOpen] = useState(false);
  const artworks = useSubjectArtworks(subjectType, subjectId, open);
  const upload = useUploadSubjectArtwork();

  // One purpose per subject type today, and the control draws whichever the
  // policy names rather than assuming `thumbnail`: the set is an operator's
  // setting, and a literal here is the defect the policy endpoint exists to
  // prevent.
  const purpose = uploadPolicy?.artwork_by_subject?.[subjectType]?.[0];
  const current = (artworks.data ?? [])[0];

  // `aspect-ratio` takes `w / h`; the policy states `w:h`.
  const ratioStyle = purpose?.ratio ? { aspectRatio: purpose.ratio.replace(':', ' / ') } : undefined;

  return (
    <details
      open={open}
      onToggle={(event) => setOpen((event.currentTarget as HTMLDetailsElement).open)}
      className="rounded-sm border border-border bg-surface p-2"
    >
      <summary className="cursor-pointer text-xs text-ink-muted">{copy.artwork.heading}</summary>

      <div className="mt-2 flex flex-wrap items-start gap-3">
        {/*
          An EXPLICIT width, not a scale step. This app's Tailwind `spacing`
          scale is the design system's tokens `0`–`10`, so `w-40` names nothing,
          emits no rule, and leaves the box to size itself from a child that is
          `h-full w-full` — which collapsed the preview to a sliver and hid
          every image it was drawn to show.
        */}
        <div
          style={ratioStyle}
          className="w-[10rem] shrink-0 overflow-hidden rounded-sm border border-border bg-bg-sunken"
        >
          {/*
            JOINED, never used raw. `backend-assets` states artwork as a
            ROOT-RELATIVE path — `/assets-artwork/public/media_asset/…` — and
            this console is a static SPA with a history fallback, so the browser
            resolves that string against the admin host, gets `index.html` back
            with a `200 text/html`, and the element decodes nothing:
            `naturalWidth` stays `0` and `onError` never fires, because a 404 it
            never got. Every other artwork reader in this app joins the origin
            through this module for exactly that reason.
          */}
          {current ? (
            <img src={artworkUrl(current.url) ?? undefined} alt="" className="h-full w-full object-cover" />
          ) : null}
        </div>

        <div className="grid grow gap-2">
          <label className="hit-target inline-flex w-fit cursor-pointer items-center rounded-sm border border-border-strong bg-surface-raised px-3 text-xs font-semibold text-ink hover:bg-surface-overlay">
            {upload.isPending ? copy.artwork.uploading : current ? copy.artwork.replace : copy.artwork.add}
            <input
              type="file"
              className="sr-only"
              accept={toAcceptAttribute(uploadPolicy?.accepted_artwork_formats)}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) upload.mutate({ subjectType, subjectId, file });
                event.target.value = '';
              }}
            />
          </label>

          <p className="mb-0 max-w-measure text-xs text-ink-muted">
            {copy.artwork.hint(
              subjectName,
              // A ratio and a pixel floor are MACHINE figures on a human line,
              // and the digits follow the surface they are read on: this page is
              // Arabic, so they are formatted rather than left ASCII.
              purpose?.ratio ?? '',
              purpose?.min_width === null || purpose?.min_width === undefined
                ? ''
                : `${formatNumber(purpose.min_width)}×${formatNumber(purpose.min_height ?? 0)}`,
              (uploadPolicy?.accepted_artwork_formats ?? []).join(' · '),
            )}
          </p>

          {/* A refusal is stated where the control is, never in a toast alone. */}
          {upload.isError ? (
            <p role="alert" className="mb-0 max-w-measure text-xs text-danger">
              {copy.artwork.failed}
            </p>
          ) : null}

          {/*
            Only for the wait running out. `:completeUpload` returns the moment
            the bytes are accepted and the crop runs behind it, so this control
            used to go quiet on success over an empty box and tell the operator
            to reload the page. The upload now HOLDS its busy state until the
            derived row appears and the picture is simply there — and what is
            left to say is the case where it never appeared, which `assets`
            reports to nobody.
          */}
          {upload.isSuccess && !current ? (
            <p role="status" className="mb-0 max-w-measure text-xs text-warning">
              {copy.artwork.notDerived}
            </p>
          ) : null}
        </div>
      </div>
    </details>
  );
}
